import React, {useState, useEffect, useMemo} from 'react';
import {Button, Form, Row, Col, Alert} from 'react-bootstrap';
import axios from './axiosConfig';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPlus, faMinus} from '@fortawesome/free-solid-svg-icons';

interface SnmpOID {
    category_id: number | null;
    metric: string;
    oid: string;
    credentials: number[];
}

interface Mapping {
    id?: number;
    original_value: string | number;   // 允许 string 或 number
    mapped_status: string;
}


function MappingConfig() {
    const [selectedMetric, setSelectedMetric] = useState<string>('');
    const [mappings, setMappings] = useState<Mapping[]>([]);
    const [existingOIDs, setExistingOIDs] = useState<SnmpOID[]>([]);

    // 新增：来自 value_mappings 表的 metric_type 列表
    const [valueMetricTypes, setValueMetricTypes] = useState<string[]>([]);

    const [alert, setAlert] = useState<{
        variant: 'success' | 'danger' | 'warning' | 'info';
        message: string;
    } | null>(null);

    /**
     * metricOptions:
     *   = SNMP OID 的 metric  +  value_mappings.metric_type
     *   再去重
     */
    const metricOptions = useMemo(() => {
        const fromSnmp = existingOIDs.map((o: SnmpOID) => o.metric);
        const fromValueMapping = valueMetricTypes;
        const all = [...fromSnmp, ...fromValueMapping];

        return Array.from(new Set(all));
    }, [existingOIDs, valueMetricTypes]);

    // 加载 SNMP OIDs
    useEffect(() => {
        axios
            .get<SnmpOID[]>('/api/snmp-oids')
            .then((response) => {
                setExistingOIDs(response.data || []);
            })
            .catch((error) => {
                console.error('Error fetching existing OIDs:', error);
                setAlert({
                    variant: 'danger',
                    message: 'Failed to load SNMP OIDs. Please try again later.'
                });
            });
    }, []);

    // 新增：加载 value_mappings 表中的 metric_type 列表
    useEffect(() => {
        axios
            .get<{ metric_types: string[] }>('/api/value-metric-types')
            .then((res) => {
                setValueMetricTypes(res.data.metric_types || []);
            })
            .catch((error) => {
                console.error('Error fetching metric types from value mappings:', error);
                // 这里不算致命错误，只在控制台打印即可，必要时可加一个轻量提示
            });
    }, []);

    // 选中 metric 时加载 / 初始化映射
// 加载 /api/mappings/:metric
    useEffect(() => {
        if (!selectedMetric) {
            setMappings([]);
            return;
        }

        if (!metricOptions.includes(selectedMetric)) {
            setMappings([{original_value: '', mapped_status: ''}]);
            return;
        }

        setMappings([]);

        axios
            .get<{ mappings: Mapping[] }>(`/api/mappings/${selectedMetric}`)
            .then((response) => {
                const list = response.data.mappings || [];
                if (list.length === 0) {
                    setMappings([{original_value: '', mapped_status: ''}]);
                } else {
                    // 🔧 后端给的是 number，这里统一转成 string，方便后面 trim()
                    const normalized = list.map((m) => ({
                        ...m,
                        original_value: String(m.original_value ?? ''),
                    }));
                    setMappings(normalized);
                }
            })
            .catch((error) => {
                console.error('Fetching mappings failed:', error);
                setAlert({
                    variant: 'danger',
                    message: 'Failed to load mappings for this metric.',
                });
                setMappings([{original_value: '', mapped_status: ''}]);
            });
    }, [selectedMetric, metricOptions]);


    const handleMappingChange = (
        index: number,
        field: keyof Mapping,
        value: string
    ) => {
        setMappings((prev) =>
            prev.map((mapping, i) =>
                i === index ? {...mapping, [field]: value} : mapping
            )
        );
    };

    const addMapping = () => {
        setMappings((prev) => [...prev, {original_value: '', mapped_status: ''}]);
    };

    const removeMapping = (index: number) => {
        setMappings((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = () => {
        setAlert(null);

        if (!selectedMetric.trim()) {
            setAlert({
                variant: 'warning',
                message: 'Please select or input a metric before saving mappings.',
            });
            return;
        }

        // ✅ 这里统一用 String(...) 再 trim
        const cleaned = mappings.filter((m) => {
            const ov = String(m.original_value ?? '').trim();
            const ms = m.mapped_status.trim();
            return ov !== '' && ms !== '';
        });

        if (cleaned.length === 0) {
            setAlert({
                variant: 'warning',
                message: 'Please add at least one valid mapping before saving.',
            });
            return;
        }

        // ✅ 校验整数
        const hasInvalidNumber = cleaned.some((m) =>
            isNaN(Number(String(m.original_value ?? '').trim()))
        );
        if (hasInvalidNumber) {
            setAlert({
                variant: 'warning',
                message: 'Original value must be an integer for all mappings.',
            });
            return;
        }

        // ✅ 发送给后端时转成字符串
        const normalizedMappings = cleaned.map((mapping) => ({
            originalValue: String(mapping.original_value ?? '').trim(),
            mappedStatus: mapping.mapped_status.trim(),
        }));

        axios
            .post('/api/value-mapping', {
                metric: selectedMetric,
                mappings: normalizedMappings,
            })
            .then(() => {
                setAlert({
                    variant: 'success',
                    message: 'Mappings have been saved successfully.',
                });
            })
            .catch((error) => {
                console.error('Submit mappings failed:', error);
                const backendMsg =
                    (error.response && error.response.data && error.response.data.error) ||
                    'Unknown error';
                setAlert({
                    variant: 'danger',
                    message: `Failed to save mappings: ${backendMsg}.`,
                });
            });
    };


    return (
        <div>
            <h2>Value Mapping Configuration</h2>

            {/* Alert bar */}
            {alert && (
                <Alert
                    variant={alert.variant}
                    onClose={() => setAlert(null)}
                    dismissible
                >
                    {alert.message}
                </Alert>
            )}

            <Form>
                <Form.Group controlId="metricSelect">
                    <Form.Label>Select Metric Type</Form.Label>

                    {/* Input + datalist: can select or type freely */}
                    <Form.Control
                        type="text"
                        value={selectedMetric}
                        onChange={(e) => setSelectedMetric(e.target.value)}
                        list="metricOptionsList"
                        placeholder="Select or type metric type"
                    />

                    <datalist id="metricOptionsList">
                        {metricOptions.length > 0
                            ? metricOptions.map((metric) => (
                                <option key={metric} value={metric}/>
                            ))
                            : null}
                    </datalist>
                </Form.Group>

                {mappings.map((mapping, index) => (
                    <Row key={mapping.id ?? index} className="mb-2">
                        <Col>
                            <Form.Group>
                                <Form.Label>Original Value</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={mapping.original_value}
                                    onChange={(e) =>
                                        handleMappingChange(index, 'original_value', e.target.value)
                                    }
                                />
                            </Form.Group>
                        </Col>
                        <Col>
                            <Form.Group>
                                <Form.Label>Mapped Status</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={mapping.mapped_status}
                                    onChange={(e) =>
                                        handleMappingChange(index, 'mapped_status', e.target.value)
                                    }
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2} className="align-self-center mt-4">
                            <Button
                                variant="danger"
                                onClick={() => removeMapping(index)}
                                disabled={mappings.length <= 1}
                            >
                                <FontAwesomeIcon icon={faMinus}/>
                            </Button>
                        </Col>
                    </Row>
                ))}

                <Button variant="success" onClick={addMapping} className="my-3 me-2">
                    <FontAwesomeIcon icon={faPlus}/> Add
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={!selectedMetric || mappings.length === 0}
                >
                    Save Mappings
                </Button>
            </Form>
        </div>
    );
}

export default MappingConfig;
